import { changeExpiry, deleteMessages, getMessageDetail } from "src/actionCreators/sentMessageActionCreators";
import {
    getMessageDetail as getMessageDetailReceived,
    changeExpiry as changeExpiryReceived,
    deleteMessage as deleteMessageReceived,
    deleteSharedMessage,
} from "src/actionCreators/receivedMessageActionCreators";

import { DeliveryType, DownloadedFrom, FolderType, MessageSource, MessageType } from "src/Core/Common/Enums";
import { IReceivedMessageModel } from "src/Core/ViewModels/Message/ReceivedMessageModel";
import { ISentMessageModel } from "src/Core/ViewModels/Message/SentMessageModel";
import { appInsightsData } from "./AppInsightsData";
import { ResourceIdLocators } from "./ResourceIdLocators";
import { IFileModel } from "src/Core/ViewModels/File/FileModel";
import { downloadAllAttachments, downloadSingleAttachment } from "src/actions/attachmentActions";
import { FolderConstants } from "src/Core/Common/Constants";

export const manageMessageType = (message: ISentMessageModel | IReceivedMessageModel, source: FolderType) => {
    if (source === FolderType.SENT) {
        if (message.folderId !== FolderType.SENT) {
            const receivedMessage = { ...message } as IReceivedMessageModel;
            receivedMessage.receivedMessageId = (message as ISentMessageModel).sentMessageId;
            receivedMessage.receivedMessageGuid = (message as ISentMessageModel).sentMessageGuid;
            return receivedMessage;
        }
        return message as ISentMessageModel;
    } else if (source === FolderType.INBOX || source === FolderType.ARCHIVE) {
        if (message.folderId === FolderType.SENT) {
            const sentMessage = { ...message } as ISentMessageModel;
            sentMessage.sentMessageId = (message as IReceivedMessageModel).receivedMessageId;
            sentMessage.sentMessageGuid = (message as IReceivedMessageModel).receivedMessageGuid;
            return sentMessage;
        }
        return message as IReceivedMessageModel;
    }
};

export const getTotalMessageCount = (messages: any[]): number => {
    return messages.reduce<number>((total: number, message: any) => {
        const threadLength: number = message.threadMessages?.length ?? 0;
        return total + 1 + threadLength;
    }, 0);
};

export const getFolderType = (folderId: number): FolderType => {
    switch (folderId) {
        case FolderType.INBOX:
            return FolderType.INBOX;
        case FolderType.SENT:
            return FolderType.SENT;
        case FolderType.SPAM:
            return FolderType.SPAM;
        default:
            return FolderType.ARCHIVE;
    }
};

export const getThreadedMessageDetail = (
    dispatch: any,
    message: ISentMessageModel | IReceivedMessageModel,
    messageDetailCallback: (data: any) => void,
    source: FolderType
) => {
    const isSentFolder = message.folderId === FolderType.SENT;
    const isInboxMessage = message.folderId === FolderType.INBOX;

    const messageId =
        source === FolderType.SENT
            ? (message as ISentMessageModel).sentMessageId
            : (message as IReceivedMessageModel).receivedMessageId;

    if (messageId > 0) {
        if (isSentFolder) {
            dispatch(getMessageDetail(messageId, FolderType.SENT, messageDetailCallback));
        } else if (isInboxMessage) {
            dispatch(
                getMessageDetailReceived(messageId, MessageType.INBOX, messageDetailCallback, FolderConstants.INBOX)
            );
        } else {
            dispatch(getMessageDetailReceived(messageId, MessageType.INBOX, messageDetailCallback, message.folderId));
        }
    }
};

export const handleChangeExpiryThreadMessages = (
    dispatch: any,
    expiresDate: Date,
    message: ISentMessageModel | IReceivedMessageModel,
    source: FolderType,
    callback?: any
) => {
    const resourceId =
        message.folderId === FolderType.INBOX ? ResourceIdLocators.Inbox.MsgDtlBtnChangeExpiry : undefined;
    const updatedMessage = manageMessageType(message, source);
    message.folderId === FolderType.SENT
        ? dispatch(changeExpiry(expiresDate, [updatedMessage as ISentMessageModel], callback))
        : dispatch(changeExpiryReceived(expiresDate, [updatedMessage as IReceivedMessageModel], callback, resourceId));
};

export const handleDeleteThreadMessages = (
    dispatch: any,
    messages: (ISentMessageModel | IReceivedMessageModel)[],
    callback: (success: boolean) => void,
    source: FolderType
) => {
    const sentMessages: ISentMessageModel[] = [];
    const receivedMessages: IReceivedMessageModel[] = [];
    const archiveMessages: IReceivedMessageModel[] = [];

    messages.forEach(msg => {
        if (msg.folderId === FolderType.SENT) {
            sentMessages.push(msg as ISentMessageModel);
        } else if (msg.folderId === FolderType.INBOX) {
            receivedMessages.push(msg as IReceivedMessageModel);
        } else {
            archiveMessages.push(msg as IReceivedMessageModel);
        }
    });

    // update sent messages
    const mapToSentMessage = (msg: ISentMessageModel): ISentMessageModel =>
    ({
        ...msg,
        sentMessageId: msg.receivedMessageId,
        sentMessageGuid: msg.receivedMessageGuid,
    } as ISentMessageModel);

    let pendingRequests = 0;
    const successFlags: boolean[] = [];

    const localCallback = (success: boolean) => {
        successFlags.push(success);
        pendingRequests--;
        if (pendingRequests === 0) {
            //Call main callback only once when all requests are done
            const allSuccess = successFlags.every(res => res === true);
            callback(allSuccess);
        }
    };

    if (source === FolderType.SENT) {
        if (sentMessages.length) {
            pendingRequests++;
            dispatch(deleteMessages(sentMessages, localCallback, ResourceIdLocators.Sent.MsgListBtnDelete));
        }
        if (receivedMessages.length) {
            pendingRequests++;
            dispatch(
                deleteMessageReceived(
                    receivedMessages.map(mapToReceivedMessage),
                    localCallback,
                    appInsightsData.MailBox.Inbox.PageTitle,
                    ResourceIdLocators.Inbox.MsgListBtnDelete
                )
            );
        }
        if (archiveMessages.length) {
            pendingRequests++;
            dispatch(
                deleteSharedMessage(
                    archiveMessages.map(mapToReceivedMessage),
                    localCallback,
                    appInsightsData.Folders.MyFiles.PageTitle,
                    ResourceIdLocators.MyFiles.MsgListBtnDelete
                )
            );
        }
    } else if (source === FolderType.INBOX) {
        if (receivedMessages.length) {
            pendingRequests++;
            dispatch(
                deleteMessageReceived(
                    receivedMessages,
                    localCallback,
                    appInsightsData.MailBox.Inbox.PageTitle,
                    ResourceIdLocators.Inbox.MsgListBtnDelete
                )
            );
        }
        if (sentMessages.length) {
            pendingRequests++;
            dispatch(
                deleteMessages(
                    sentMessages.map(mapToSentMessage),
                    localCallback,
                    ResourceIdLocators.Sent.MsgListBtnDelete
                )
            );
        }
    } else if (source === FolderType.ARCHIVE) {
        if (messages.length) {
            pendingRequests++;
            dispatch(
                deleteSharedMessage(
                    messages as IReceivedMessageModel[],
                    localCallback,
                    appInsightsData.Folders.MyFiles.PageTitle,
                    ResourceIdLocators.MyFiles.MsgListBtnDelete
                )
            );
        }
    }
};

export const mapToReceivedMessage = (msg: IReceivedMessageModel): IReceivedMessageModel =>
({
    ...msg,
    receivedMessageId: msg.sentMessageId,
    receivedMessageGuid: msg.sentMessageGuid,
} as IReceivedMessageModel);

export const handleThreadsDownloadSingleAttachment = (
    dispatch: any,
    fileId: number,
    physicalPath: string,
    messageId: number,
    fileName: string,
    senderEmail: string,
    receivedMessageGuid: string,
    messageSource: MessageSource,
    folderId: number,
    callback: any
) => {
    let updatedSenderEmail = "";
    let updatedReceivedMessageGuid = "";
    let updatedMessageSource = MessageSource.MAILBOX;
    let downloadedFrom = DownloadedFrom.MPI_Download_SentItem_Single_File;
    let pageTitle = appInsightsData.MailBox.Sent.PageTitle;
    let resourceId = ResourceIdLocators.Sent.MsgDtlBtnDownloadFile;

    if (folderId !== FolderType.SENT) {
        updatedSenderEmail = senderEmail;
        updatedReceivedMessageGuid = receivedMessageGuid;
        updatedMessageSource = messageSource;
        downloadedFrom = DownloadedFrom.MPI_Download_Inbox_Single_File;
        pageTitle =
            folderId === FolderType.INBOX
                ? appInsightsData.MailBox.Inbox.PageTitle
                : appInsightsData.Folders.MyFiles.PageTitle;
        resourceId =
            folderId === FolderType.INBOX
                ? ResourceIdLocators.Inbox.MsgDtlBtnDownloadFile
                : ResourceIdLocators.MyFiles.MsgDtlBtnDownloadFile;
    }
    dispatch(
        downloadSingleAttachment(
            fileId,
            physicalPath,
            messageId,
            fileName,
            updatedSenderEmail,
            updatedReceivedMessageGuid,
            updatedMessageSource,
            downloadedFrom,
            callback,
            pageTitle,
            resourceId
        )
    );
};

export const handleThreadsDownloadAllAttachments = (
    dispatch: any,
    files: IFileModel[],
    messageId: number,
    senderEmail: string,
    receivedMessageGuid: string,
    messageSource: MessageSource,
    folderId: number,
    callback: any
) => {
    let updatedSenderEmail = "";
    let updatedReceivedMessageGuid = "";
    let updatedMessageSource = MessageSource.MAILBOX;
    let downloadedFrom = DownloadedFrom.MPI_Download_SentItem_Multiple_File;
    let pageTitle = appInsightsData.MailBox.Sent.PageTitle;
    let resourceId = ResourceIdLocators.Sent.MsgDtlBtnDownloadFiles;
    if (folderId !== FolderType.SENT) {
        updatedSenderEmail = senderEmail;
        updatedReceivedMessageGuid = receivedMessageGuid;
        updatedMessageSource = messageSource;
        downloadedFrom = DownloadedFrom.MPI_Download_Inbox_Multiple_File;
        pageTitle =
            folderId === FolderType.INBOX
                ? appInsightsData.MailBox.Inbox.PageTitle
                : appInsightsData.Folders.MyFiles.PageTitle;
        resourceId =
            folderId === FolderType.INBOX
                ? ResourceIdLocators.Inbox.MsgDtlBtnDownloadFiles
                : ResourceIdLocators.MyFiles.MsgDtlBtnDownloadFiles;
    }

    dispatch(
        downloadAllAttachments(
            files,
            messageId,
            updatedSenderEmail,
            updatedReceivedMessageGuid,
            updatedMessageSource,
            downloadedFrom,
            callback,
            pageTitle,
            resourceId
        )
    );
};

export const areThreadsValid = (
    selectedMessages: IReceivedMessageModel[],
    allMessages: IReceivedMessageModel[]
): { allThreadsValid: boolean; isSingleQuickReplyMessagePresent: boolean } => {
    const result: { allThreadsValid: boolean; isSingleQuickReplyMessagePresent: boolean } = {
        allThreadsValid: true,
        isSingleQuickReplyMessagePresent: false,
    };
    selectedMessages.forEach(selMsg => {
        if (!selMsg.threadId) {
            return;
        }

        //first matching threadId in props.messages will be the parent message
        const parentMsg = allMessages.find(msg => msg.threadId === selMsg.threadId);
        if (!parentMsg) {
            result.allThreadsValid = false;
            return;
        }

        // If the thread contains only one message and it's a quick reply, it should not be moved to a folder.
        if (
            (!parentMsg.threadMessages || !parentMsg.threadMessages?.length) &&
            parentMsg.deliveryType == DeliveryType.QuickReply
        ) {
            result.isSingleQuickReplyMessagePresent = true;
        }

        const allThreadMessages = [parentMsg, ...(parentMsg.threadMessages || [])];
        result.allThreadsValid =
            result.allThreadsValid &&
            allThreadMessages.every(threadMsg =>
                selectedMessages.some(m => m.receivedMessageId === threadMsg.receivedMessageId)
            );
        return;
    });

    return result;
};

/**
 * 
 * @param folderId - folderId of the folder, 2 for sent folder, otherwise archive folder
 * @description Returns the folder type for an archive folder based on the provided folderId.
 * @returns FolderType - Returns FolderType.SENT if folderId is 2, otherwise returns FolderType.ARCHIVE.
 */
export const getFolderTypeForArchiveFolder = (folderId: number): FolderType => {
    if (folderId == 2) {
        return FolderType.SENT;
    } else {
        return FolderType.ARCHIVE;
    }
};